import { setCookie as setClientCookie } from "cookies-next/client";
import { setCookie as setServerCookie } from "cookies-next/server";
import type { NextApiRequest, NextApiResponse } from "next";

export const PREFERRED_SIGN_IN_PROVIDER_COOKIE = "preferred-signin-provider";

const PREFERRED_SIGN_IN_PROVIDER_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export type PreferredSignInProvider = "google" | "email" | "qa-login";

const PREFERRED_SIGN_IN_PROVIDERS = new Set<PreferredSignInProvider>([
  "google",
  "email",
  "qa-login",
]);

export function parsePreferredSignInProvider(
  value: string | undefined | null
): PreferredSignInProvider | null {
  if (!value) {
    return null;
  }

  if (PREFERRED_SIGN_IN_PROVIDERS.has(value as PreferredSignInProvider)) {
    return value as PreferredSignInProvider;
  }

  return null;
}

export function getPreferredSignInProviderFromRequest(req: {
  cookies: Partial<{ [key: string]: string }>;
}): PreferredSignInProvider | null {
  const value = req.cookies[PREFERRED_SIGN_IN_PROVIDER_COOKIE];
  return parsePreferredSignInProvider(value);
}

export function setPreferredSignInProvider(provider: PreferredSignInProvider) {
  setClientCookie(PREFERRED_SIGN_IN_PROVIDER_COOKIE, provider, {
    maxAge: PREFERRED_SIGN_IN_PROVIDER_MAX_AGE_SECONDS,
    path: "/",
    sameSite: "lax",
  });
}

export async function setPreferredSignInProviderOnServer(
  provider: PreferredSignInProvider,
  context: { req: NextApiRequest; res: NextApiResponse }
): Promise<void> {
  await setServerCookie(PREFERRED_SIGN_IN_PROVIDER_COOKIE, provider, {
    req: context.req,
    res: context.res,
    ...getPreferredSignInProviderCookieOptions(),
  });
}

export function toPreferredSignInProvider(
  provider: string | undefined | null
): PreferredSignInProvider | null {
  if (provider === "google" || provider === "email") {
    return provider;
  }

  if (provider === "qa-login") {
    return "qa-login";
  }

  return null;
}

function getPreferredSignInProviderCookieOptions() {
  return {
    maxAge: PREFERRED_SIGN_IN_PROVIDER_MAX_AGE_SECONDS,
    path: "/",
    sameSite: "lax" as const,
  };
}
